import * as React from 'react';
import { de } from 'date-fns/locale';
import { CalendarDays, X } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const isoOf = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const dateOf = (iso) => {
  if (!iso) return undefined;
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d;
};
const fmt = (iso) => {
  const d = dateOf(iso);
  if (!d) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
};
export { isoOf, dateOf, fmt };

// Akzeptiert: 15.07.2026, 15.7.26, 15.07, 15072026, 2026-07-15
export const parseGermanDate = (text) => {
  const t = (text || '').trim();
  if (!t) return null;
  let m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12);
    return d.getMonth() === Number(m[2]) - 1 ? d : undefined;
  }
  m = t.match(/^(\d{1,2})\.(\d{1,2})\.?(\d{2,4})?\.?$/) || t.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (!m) return undefined;
  const day = Number(m[1]);
  const month = Number(m[2]);
  let year = m[3] !== undefined ? Number(m[3]) : new Date().getFullYear();
  if (m[3] !== undefined && m[3].length <= 2) year += year < 50 ? 2000 : 1900;
  const d = new Date(year, month - 1, day, 12);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return undefined;
  return d;
};

// Vollständige Eingabe mit vierstelligem Jahr — Teileingaben wie "15.7" bleiben dem Blur/Enter
// überlassen, damit beim Tippen keine Zwischenjahre (z. B. "15.7.2" -> 2002) gemeldet werden.
const COMPLETE_DATE_RE = /^(?:\d{1,2}\.\d{1,2}\.\d{4}|\d{2}\d{2}\d{4}|\d{4}-\d{1,2}-\d{1,2})$/;

// Drop-in-Ersatz für <input type="date">: onChange erhält { target: { value: 'YYYY-MM-DD' } }
// Tastatur-Eingabe (TT.MM.JJJJ) UND Klick-Kalender möglich.
const DateInput = React.forwardRef(({ value, onChange, className, min, max, disabled, placeholder, clearable = true, onFocus, onEnterCommit, ...rest }, ref) => {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState(fmt(value));
  const emit = (v) => onChange && onChange({ target: { value: v } });
  const minD = dateOf(min);
  const maxD = dateOf(max);

  React.useEffect(() => {
    setText(fmt(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commitText = () => {
    const parsed = parseGermanDate(text);
    if (parsed === null) { emit(''); setText(''); return; }
    if (parsed === undefined) { setText(fmt(value)); return; }
    emit(isoOf(parsed));
    setText(fmt(isoOf(parsed)));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div
          title={'Datum eintippen (z. B. 15.8.26), ↑/↓ = Tag vor/zurück, Shift+↑/↓ = Woche, Kalender über das Icon'}
          className={cn(
            'flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
            disabled && 'cursor-not-allowed opacity-50',
            className
          )}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              tabIndex={-1}
              disabled={disabled}
              className="shrink-0 text-slate-400 hover:text-slate-700 disabled:pointer-events-none"
              aria-label="Kalender öffnen"
            >
              <CalendarDays className="w-4 h-4" />
            </button>
          </PopoverTrigger>
          <input
            ref={ref}
            type="text"
            inputMode="numeric"
            {...rest}
            value={text}
            disabled={disabled}
            placeholder={placeholder || 'TT.MM.JJJJ'}
            onChange={(e) => {
              const next = e.target.value;
              setText(next);
              // Safari gibt Buttons beim Klick keinen Fokus, dadurch bleibt onBlur aus und ein
              // fertig getipptes Datum erreichte das Formular nie.
              if (COMPLETE_DATE_RE.test(next.trim())) {
                const parsed = parseGermanDate(next);
                if (parsed) emit(isoOf(parsed));
              }
            }}
            onFocus={(e) => {
              // Sofort überschreibbar: markieren statt erst löschen müssen
              e.target.select();
              onFocus?.(e);
            }}
            onBlur={() => { commitText(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                commitText();
                e.target.blur();
                // Enter speichert danach über den gemeinsamen Kurzbefehl, sofern eine
                // Speichern-Schaltfläche zur Bearbeitung gehört.
                if (onEnterCommit) {
                  queueMicrotask(() => onEnterCommit());
                }
                return;
              }
              if (e.key === 'Escape') { setText(fmt(value)); e.target.blur(); }
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
                const base = parseGermanDate(text) || dateOf(value) || new Date();
                const step = e.key === 'ArrowUp' ? 1 : -1;
                base.setDate(base.getDate() + (e.shiftKey ? step * 7 : step));
                const iso = isoOf(base);
                emit(iso);
                setText(fmt(iso));
              }
            }}
            className="flex-1 min-w-0 bg-transparent outline-none border-none p-0 text-inherit placeholder:text-muted-foreground disabled:cursor-not-allowed"
            {...rest}
          />
          {clearable && value && !disabled && (
            <button
              type="button"
              aria-label="Datum löschen"
              className="shrink-0 text-slate-400 hover:text-slate-700"
              onClick={(e) => { e.stopPropagation(); emit(''); setText(''); setOpen(false); }}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </PopoverAnchor>
      <PopoverContent side="bottom" align="start" className="w-auto p-0 z-[100] bg-white" onOpenAutoFocus={(e) => e.preventDefault()}>
        <Calendar
          mode="single"
          locale={de}
          selected={dateOf(value)}
          defaultMonth={dateOf(value) || new Date()}
          disabled={(d) => (minD && d < minD) || (maxD && d > maxD) || false}
          onSelect={(d) => { if (d) { emit(isoOf(d)); setText(fmt(isoOf(d))); setOpen(false); } }}
        />
      </PopoverContent>
    </Popover>
  );
});
DateInput.displayName = 'DateInput';

export { DateInput };
export default DateInput;
